/**
 * BD Quick Cart - Enhanced cart drawer functionality
 * Handles cart operations, progress bar, badges, and upsells
 */

class BDQuickCart {
  constructor() {
    this.cart = null;
    this.isOpen = false;
    this.isLoading = false;
    this.freeGiftThreshold = parseFloat(document.querySelector('[data-free-gift-threshold]')?.dataset.freeGiftThreshold) || 50;
    this.freeGiftProduct = document.querySelector('[data-free-gift-product]')?.dataset.freeGiftProduct || null;
    this.autoAddFreeGift = document.querySelector('[data-bd-quick-cart]')?.dataset.autoAddFreeGift !== 'false';
    this.freeGiftAdded = false;
    this.isRemovingFreeGift = false;
    this.autoRefreshIntervalMs = 4000; // 4s — within 3–5s target while drawer is open
    this.autoRefreshTimer = null;
    this.isAutoRefreshing = false;
    this._isCartMutating = false;
    this._normalizingGift = false;
    this.autoRefreshPausedUntil = 0;
    this._lastRenderedLineKeys = [];
    this._keepDrawerOpen = false;
    this._blockOverlayCloseUntil = 0;
    this._deferredFreeGiftCheck = false;
    this._isRefreshingSection = false;
    this._syncingDependentApps = false;
    this._reconcilingCart = false;
    this._deferredReconcile = false;
    this._renderGuardInstalled = false;
    this._scrollLockY = null;
    this._deferCartUiPaint = false;
    this._productDataRequests = new Map();
    this._itemAddedQueue = Promise.resolve();

    this.selectors = {
      cart: '[data-bd-quick-cart]',
      overlay: '[data-overlay]',
      closeBtn: '[data-close-icon]',
      items: '[data-items]',
      empty: '[data-empty]',
      footer: '[data-footer]',
      subtotal: '[data-subtotal]',
      qtyDropdown: '[data-qty-dropdown]',
      qtyTrigger: '[data-qty-trigger]',
      qtyOptions: '[data-qty-options]',
      qtyOption: '[data-qty-option]',
      qtyValue: '[data-qty-value]',

      addUpsell: '[data-add-upsell]',
      variantSelect: '[data-upsell-variant-select]',
      checkoutBtn: '[data-checkout-button]',
      progressFill: '.bd-quick-cart__progress-fill',
      progressText: '.bd-quick-cart__progress-text',
      giftTemplate: '#bd-free-gift-template'
    };

    this.init();
  }

  lockPageScroll() {
    if (this._scrollLockY === null) {
      this._scrollLockY = window.scrollY;
    }
    document.body.style.overflow = 'hidden';
  }

  unlockPageScroll() {
    const scrollY = this._scrollLockY ?? window.scrollY;
    this._scrollLockY = null;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  init() {
    this.bindEvents();
    this.observeCartOpenState();
    this.installDrawerClassWatcher();
    this.installSectionRefreshGuard();
    this.installRenderGuard();
    this.loadCart();
  }

  installRenderGuard() {
    if (this._renderGuardInstalled) return;
    this._renderGuardInstalled = true;

    const originalRender = this.render.bind(this);
    this.render = (source = 'unknown', options = {}) => {
      const drawerActive = this._keepDrawerOpen || this.isOpen;
      if (drawerActive && !options?.force && source === 'unknown') {
        this.scheduleCartReconcile('render:guard-unknown', { force: true });
        return;
      }
      return originalRender(source, options);
    };
  }

  installSectionRefreshGuard() {
    const container = document.querySelector('#shopify-section-bd-quick-cart_section');
    if (!container) return;

    this.patchSectionInnerHtml(container);

    if (this._sectionRefreshGuardEl === container) return;

    if (this._sectionRefreshGuardObserver) {
      this._sectionRefreshGuardObserver.disconnect();
    }

    this._sectionRefreshGuardEl = container;
    this._sectionRefreshGuardObserver = new MutationObserver(() => {
      if (!this._keepDrawerOpen && !this.isOpen) return;
      if (this._isRefreshingSection) return;

      this.bindCartOpenObserver();
      this.syncDrawerOpenState('section-refresh-guard');
    });

    this._sectionRefreshGuardObserver.observe(container, { childList: true });
  }

  patchSectionInnerHtml(container) {
    if (container.__bdInnerHtmlPatched) {
      this.patchSectionDomMethods(container);
      return;
    }

    const descriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    if (!descriptor?.set) return;

    const self = this;
    Object.defineProperty(container, 'innerHTML', {
      configurable: true,
      enumerable: descriptor.enumerable,
      get() {
        return descriptor.get.call(container);
      },
      set(value) {
        const drawerShouldStayOpen = self._keepDrawerOpen || self.isOpen;
        if (drawerShouldStayOpen && !self._isRefreshingSection) {
          void self.softRefreshCart({
            skipFreeGiftCheck: true,
            source: 'innerHTML-blocked',
            fetchCart: true
          });
          self.syncDrawerOpenState('innerHTML-blocked');
          return;
        }

        descriptor.set.call(container, value);
        self.patchSectionInnerHtml(container);
      }
    });

    container.__bdInnerHtmlPatched = true;
    this.patchSectionDomMethods(container);
  }

  patchSectionDomMethods(container) {
    if (container.__bdDomMethodsPatched) return;
    const self = this;

    const blockIfDrawerOpen = (source) => {
      if ((self._keepDrawerOpen || self.isOpen) && !self._isRefreshingSection) {
        void self.softRefreshCart({ skipFreeGiftCheck: true, source, fetchCart: true });
        self.syncDrawerOpenState(source);
        return true;
      }
      return false;
    };

    const origReplaceChildren = container.replaceChildren.bind(container);
    container.replaceChildren = (...nodes) => {
      if (blockIfDrawerOpen('patch:replaceChildren')) return;
      const result = origReplaceChildren(...nodes);
      self.patchSectionInnerHtml(container);
      return result;
    };

    const origReplaceWith = container.replaceWith.bind(container);
    container.replaceWith = (...nodes) => {
      if (blockIfDrawerOpen('patch:replaceWith')) return;
      return origReplaceWith(...nodes);
    };

    container.__bdDomMethodsPatched = true;
  }

  flushDeferredGiftSideEffects() {
    if (!this._deferredFreeGiftCheck) return;
    this._deferredFreeGiftCheck = false;

    if (this._isCartMutating || this._normalizingGift) {
      this._deferredFreeGiftCheck = true;
      return;
    }

    this.checkFreeGift();
  }

  installDrawerClassWatcher() {
    const cartEl = document.querySelector(this.selectors.cart);
    if (!cartEl) return;

    if (this._drawerClassWatchEl === cartEl) return;

    if (this._drawerClassWatchObserver) {
      this._drawerClassWatchObserver.disconnect();
    }

    this._drawerClassWatchEl = cartEl;
    this._drawerClassWatchObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName !== 'class') return;

        const hadOpen = mutation.oldValue?.includes('is-open') ?? false;
        const hasOpen = cartEl.classList.contains('is-open');

        if (hadOpen === hasOpen) return;

        if (hadOpen && !hasOpen && (this._keepDrawerOpen || this.isOpen)) {
          this.syncDrawerOpenState('class-watcher-restore');
        }
      });
    });

    this._drawerClassWatchObserver.observe(cartEl, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true
    });
  }

  getLineItemKey(item) {
    if (!item) return '';
    return String(item.key || item.variant_id);
  }

  findCartItem(lineKey) {
    const key = String(lineKey);
    return this.cart?.items?.find(item => this.getLineItemKey(item) === key) || null;
  }

  findQtyDropdown(lineKey) {
    const key = String(lineKey);
    return Array.from(document.querySelectorAll(this.selectors.qtyDropdown))
      .find(dropdown => (dropdown.dataset.lineKey || dropdown.dataset.itemId) === key) || null;
  }

  updateQuantityDropdown(dropdown, quantity) {
    if (!dropdown) return;

    const valueEl = dropdown.querySelector(this.selectors.qtyValue);
    if (valueEl) {
      valueEl.textContent = quantity;
    }

    dropdown.querySelectorAll(this.selectors.qtyOption).forEach(option => {
      const qty = parseInt(option.dataset.qtyOption, 10);
      option.classList.toggle(
        'bd-quick-cart__qty-option--selected',
        qty === quantity
      );
    });
  }

  getFreeGiftItems(items = this.cart?.items) {
    return (items || []).filter(item => this.isFreeGiftItem(item));
  }

  getThresholdFreeGiftItems(items = this.cart?.items) {
    return this.getFreeGiftItems(items).filter(item => !this.isGiftboxPromoItem(item));
  }

  isFreeGiftItem(item) {
    if (!item) return false;
    if (item.properties?._free_gift === 'true') return true;

    const giftRef = String(this.freeGiftProduct || '');
    if (giftRef) {
      if (item.product_handle === giftRef || item.handle === giftRef) return true;
      if (String(item.variant_id) === giftRef) return true;
    }

    if (Number(item.line_price) === 0 && Number(item.price) === 0) {
      const title = item.product_title || '';
      if (/geschenk/i.test(title)) return true;
      if (this.isGiftboxPromoItem(item)) return true;
    }

    return false;
  }

  isGiftboxPromoItem(item) {
    if (!item || Number(item.price) !== 0 || Number(item.line_price) !== 0) return false;

    const title = (item.product_title || '').toLowerCase();
    if (/(geldbörse|geldborse|wallet|börse|borse)/i.test(title)) return false;
    if (/schlüssel|schluessel|anhänger|anhaenger|keychain/i.test(title)) return true;

    const props = item.properties || {};
    return Object.keys(props).some(key => /gift|giftbox|free|gratis/i.test(key));
  }

  syncPromoGiftQtyDisplays() {
    this.getFreeGiftItems().forEach(item => {
      const lineKey = this.getLineItemKey(item);
      const row = document.querySelector(`[data-line-key="${lineKey}"]`);
      const giftQty = row?.querySelector('.bd-quick-cart__gift-qty');
      if (giftQty) {
        giftQty.textContent = String(item.quantity);
      }
    });
  }

  async syncGiftboxPromoQuantities() {
    if (typeof window.GIFTBOX_CUSTOM_UPDATE_CART !== 'function') return false;

    const previousItems = [...(this.cart?.items || [])];

    try {
      await window.GIFTBOX_CUSTOM_UPDATE_CART();
      const response = await fetch('/cart.js');
      if (!response.ok) return false;

      this.cart = await response.json();
      this.preserveEnrichedCartItems(previousItems);
      await this.enrichCartWithMetafields();
      return true;
    } catch (error) {
      console.error('Giftbox promo qty sync failed:', error);
      return false;
    }
  }

  pauseAutoRefresh(ms = 8000) {
    this.autoRefreshPausedUntil = Date.now() + ms;
  }

  isAutoRefreshPaused() {
    return Date.now() < this.autoRefreshPausedUntil;
  }

  hasCartStructureChanged(nextCart = this.cart) {
    const nextKeys = (nextCart?.items || []).map(item => this.getLineItemKey(item)).join('|');
    return nextKeys !== this._lastRenderedLineKeys.join('|');
  }

  rememberRenderedLineKeys() {
    this._lastRenderedLineKeys = (this.cart?.items || []).map(item => this.getLineItemKey(item));
  }

  cartNeedsStructuralRender(cart = this.cart) {
    const itemsContainer = document.querySelector(this.selectors.items);
    if (!itemsContainer) return !!(cart?.items?.length);

    const domLineKeys = new Set();
    itemsContainer.querySelectorAll('[data-line-key]').forEach(el => {
      const key = el.getAttribute('data-line-key');
      if (key) domLineKeys.add(key);
    });

    const cartLineKeys = (cart?.items || []).map(item => this.getLineItemKey(item));
    if (cartLineKeys.length !== domLineKeys.size) return true;
    return cartLineKeys.some(key => !domLineKeys.has(key));
  }

  getDomLineItemCount() {
    const itemsContainer = document.querySelector(this.selectors.items);
    if (!itemsContainer) return 0;
    return itemsContainer.querySelectorAll('.bd-quick-cart__item').length;
  }

  needsFullDrawerRender(cart = this.cart) {
    const cartItemCount = cart?.items?.length || 0;
    return this.hasCartStructureChanged(cart)
      || this.cartNeedsStructuralRender(cart)
      || cartItemCount !== this.getDomLineItemCount();
  }

  removeStaleLineItemsFromDom() {
    const itemsContainer = document.querySelector(this.selectors.items);
    if (!itemsContainer || !this.cart) return;

    const cartLineKeys = new Set(
      (this.cart.items || []).map(item => this.getLineItemKey(item))
    );

    itemsContainer.querySelectorAll('[data-line-key]').forEach(el => {
      const key = el.getAttribute('data-line-key');
      if (key && !cartLineKeys.has(key)) {
        el.closest('.bd-quick-cart__item')?.remove();
      }
    });
  }

  applyCartStateToUi({ source = 'unknown', allowFullRender = true, force = false } = {}) {
    if (this._deferCartUiPaint && !force) return;

    const needsFull = (allowFullRender && this.needsFullDrawerRender(this.cart)) || force;

    if (needsFull) {
      this.render(source, { force });
      return;
    }

    this.syncQuantityDisplaysFromCart(null);
    this.updateProgressVisual();
    this.updateCartIcon();
  }

  bindCartOpenObserver() {
    const cartEl = document.querySelector(this.selectors.cart);
    if (!cartEl) return;

    if (this._cartOpenObserverTarget === cartEl) return;

    if (this._cartOpenObserver) {
      this._cartOpenObserver.disconnect();
    }

    this._cartOpenObserverTarget = cartEl;
    this._cartOpenObserver = new MutationObserver(() => {
      if (this._isCartMutating) return;

      const isOpen = cartEl.classList.contains('is-open');

      // Only sync external opens — never auto-close from DOM mutations
      if (isOpen && !this.isOpen) {
        this.isOpen = true;
        this._keepDrawerOpen = true;
        this.lockPageScroll();
        this.startAutoRefresh();
      }
    });

    this._cartOpenObserver.observe(cartEl, {
      attributes: true,
      attributeFilter: ['class']
    });

    this.installDrawerClassWatcher();
  }

  bindEvents() {
    // Open cart triggers
    document.addEventListener('click', (e) => {
      const target = e.target.matches('[data-open-cart]') ? e.target : e.target.closest('[data-open-cart]');
      if (target) {
        e.preventDefault();

        // If button also has data-add-to-cart, handle add to cart first
        if (target.hasAttribute('data-add-to-cart') && target.type === 'button') {
          this.handleAddToCartAndOpen(target);
        } else {
          this.open();
        }
      }
    });

    // Close cart
    document.addEventListener('click', (e) => {
      const isCloseTarget = e.target.matches(this.selectors.overlay)
        || e.target.matches(this.selectors.closeBtn)
        || e.target.closest(this.selectors.closeBtn);

      if (!isCloseTarget) return;

      if (this.isLoading || this._isCartMutating || this._normalizingGift) {
        return;
      }

      if (Date.now() < this._blockOverlayCloseUntil) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      this.close('overlay-or-close-button');
    }, true);

    // Quantity dropdown toggle
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.qtyTrigger) || e.target.closest(this.selectors.qtyTrigger)) {
        e.preventDefault();
        e.stopPropagation();
        const trigger = e.target.closest(this.selectors.qtyTrigger);
        const dropdown = trigger.closest(this.selectors.qtyDropdown);
        this.toggleQuantityDropdown(dropdown);
        return;
      }

      // Close dropdowns when clicking outside
      if (!e.target.closest(this.selectors.qtyDropdown)) {
        this.closeAllQuantityDropdowns();
      }
    });

    // Quantity option selection
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.qtyOption) || e.target.closest(this.selectors.qtyOption)) {
        e.preventDefault();
        e.stopPropagation();
        const option = e.target.closest(this.selectors.qtyOption);
        const dropdown = option.closest(this.selectors.qtyDropdown);
        const lineKey = dropdown.dataset.lineKey || dropdown.dataset.itemId;
        const newQty = parseInt(option.dataset.qtyOption) || 0;

        this.selectQuantityOption(dropdown, option, newQty);
        this.setQuantity(lineKey, newQty);
      }
    });


    // ADD: Single variant dropdown → keeps the existing upsell button and only updates its attributes
    document.addEventListener('change', (e) => {
      if (e.target.matches(this.selectors.variantSelect)) {
        this.updateUpsellButtonForSelect(e.target);
      }
    });


    // Add upsell
    document.addEventListener('click', (e) => {
      if (e.target.matches(this.selectors.addUpsell) || e.target.closest(this.selectors.addUpsell)) {
        const btn = e.target.closest(this.selectors.addUpsell);
        const variantId = btn.dataset.variantId;
        this.addUpsell(variantId);
      }
    });

    // Upsell carousel scroll buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-scroll-prev]')) {
        e.preventDefault();
        this.scrollUpsells('prev');
      }
      if (e.target.closest('[data-scroll-next]')) {
        e.preventDefault();
        this.scrollUpsells('next');
      }
    });

    // Remove Gift buttons
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.remove-gift');
      if (removeBtn) {
        e.preventDefault();
        const lineKey = removeBtn.dataset.lineKey || removeBtn.dataset.itemId;
        this.removeItem(lineKey);
      }
    });

    // Touch/swipe support for upsell carousel
    this.initUpsellCarouselTouch();

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close('escape-key');
      }
    });

    // Cart update events
    document.addEventListener('cart:updated', (e) => {
      if (this._isCartMutating) return;
      if (e.detail?.source === 'bd-quick-cart') return;

      const cart = e.detail?.cart ?? e.detail;
      if (!cart || cart.item_count === undefined) return;

      void this.handleExternalCartUpdate(cart);
    });

    // Add to cart events
    document.addEventListener('cart:item-added', () => {
      void this.handleCartItemAdded();
    });

    // ADD: Initialize defaults on first load + observe dynamic changes
    this.initializeVariantSelectDefaults();
    this.observeUpsellsForVariantSelects();
  }

  observeCartOpenState() {
    this.bindCartOpenObserver();
  }

  // Returns an object with Feature_1_* and Feature_2_* taken from the DOM
  collectTopTwoFeatureProps(fromEl) {
    // Scope search near the clicked button if possible
    const root = fromEl?.closest('[data-section-type]') || document;

    const items = root.querySelectorAll('.product-feature-list__items .product-feature-list__item');
    const props = {};
    let idx = 0;

    for (const item of items) {
      if (idx >= 2) break;
      const textEl = item.querySelector('.product-feature-list__text');
      const imgEl = item.querySelector('.product-feature-list__icon img');

      const text = (textEl?.textContent || '').trim();
      const icon = imgEl?.getAttribute('src') || '';
      const alt = imgEl?.getAttribute('alt') || '';

      const i = idx + 1;
      if (text) props[`Feature_${i}_text`] = text;
      if (icon) props[`Feature_${i}_icon`] = icon;
      if (alt) props[`Feature_${i}_icon_alt`] = alt;

      idx++;
    }
    return props;
  }


  async syncPromoGiftAfterDependentApps(source = 'syncPromoGiftAfterDependentApps') {
    const runGiftbox = async () => {
      if (typeof window.GIFTBOX_CUSTOM_UPDATE_CART !== 'function') return false;
      try {
        await window.GIFTBOX_CUSTOM_UPDATE_CART();
        return true;
      } catch (error) {
        console.error('Giftbox promo sync failed:', error);
        return false;
      }
    };

    const refreshCartFromServer = async () => {
      const response = await fetch('/cart.js');
      if (!response.ok) return false;

      const previousItems = [...(this.cart?.items || [])];
      this.cart = await response.json();
      this.preserveEnrichedCartItems(previousItems);
      await this.enrichCartWithMetafields();
      return true;
    };

    const giftboxAvailable = await runGiftbox();
    await refreshCartFromServer();

    let attempts = 0;
    while (
      giftboxAvailable &&
      this.getCartTotalExcludingFreeGifts() > 0 &&
      this.getFreeGiftItems().length === 0 &&
      attempts < 4
    ) {
      attempts += 1;
      if (giftboxAvailable) {
        await runGiftbox();
      }
      await refreshCartFromServer();
    }

    const stillMissingPromoGift = this.getCartTotalExcludingFreeGifts() > 0 && this.getFreeGiftItems().length === 0;

    if (!stillMissingPromoGift) {
      await this.ensureFreeGiftQuantityIsOne({ allowDuringMutation: true });
    }

    this.applyCartStateToUi({ source, allowFullRender: true, force: true });
    this.updateProgressVisual();
    this.updateCartIcon();
    this.rememberRenderedLineKeys();

    if (giftboxAvailable && stillMissingPromoGift) {
      this.scheduleCartReconcile(`${source}:await-gift`, { force: true });
    }
  }

  handleCartItemAdded() {
    this._keepDrawerOpen = true;
    this.open();

    // Serialize additions so overlapping app responses cannot overwrite newer cart state.
    this._itemAddedQueue = this._itemAddedQueue
      .then(() => this.refreshAddedItem())
      .catch(error => console.error('Error refreshing added cart item:', error));
    return this._itemAddedQueue;
  }

  async refreshAddedItem() {
    try {
      this.setLoading(true);
      // Paint the confirmed cart after one read; gifts must not delay the paid item.
      await this.loadCart('cart:item-added', { fast: true });
      this.setLoading(false);
      await this.syncPromoGiftAfterDependentApps('cart:item-added:promo-sync');
    } finally {
      this.setLoading(false);
      this.pauseAutoRefresh(6000);
    }
  }

  ensureDrawerOpen(source = 'ensureDrawerOpen') {
    this.syncDrawerOpenState(source);
  }

  restoreDrawerOpenSilently(source = 'restore') {
    const cartElement = document.querySelector(this.selectors.cart);
    if (!cartElement) return false;

    this._keepDrawerOpen = true;
    this.isOpen = true;

    if (cartElement.classList.contains('is-open')) {
      this.lockPageScroll();
      return false;
    }

    cartElement.classList.add('bd-quick-cart--no-transition', 'is-open');
    this.lockPageScroll();
    void cartElement.offsetHeight;
    cartElement.classList.remove('bd-quick-cart--no-transition');

    if (!this.autoRefreshTimer) {
      this.startAutoRefresh();
    }

    return true;
  }

  syncDrawerOpenState(source = 'syncDrawerOpenState') {
    if (!this._keepDrawerOpen && !this.isOpen) {
      return;
    }

    const cartElement = document.querySelector(this.selectors.cart);
    if (cartElement?.classList.contains('is-open')) {
      this._keepDrawerOpen = true;
      this.isOpen = true;
      this.lockPageScroll();
      return;
    }

    this.restoreDrawerOpenSilently(source);
  }

  async handleExternalCartUpdate(cart) {
    await this.applyExternalCartUpdate(cart, { source: 'cart:updated-external' });
  }

  async applyExternalCartUpdate(cart, { source = 'external' } = {}) {
    if (!cart || cart.item_count === undefined) return;

    if (this.getCartFingerprint(cart) === this.getCartFingerprint(this.cart)) {
      if (this._keepDrawerOpen || this.isOpen) {
        this.syncDrawerOpenState(source);
      }
      return;
    }

    const previousItems = [...(this.cart?.items || [])];

    if (this._deferCartUiPaint) {
      const incomingCount = cart.item_count ?? 0;
      const currentCount = this.cart?.item_count ?? 0;
      if (incomingCount >= currentCount) {
        this.cart = cart;
        await this.ensureFreeGiftQuantityIsOne({ allowDuringMutation: true });
        this.preserveEnrichedCartItems(previousItems);
        await this.enrichCartWithMetafields();
      }
      if (this._keepDrawerOpen || this.isOpen) {
        this.syncDrawerOpenState(source);
      }
      this.pauseAutoRefresh(6000);
      return;
    }

    this.cart = cart;
    await this.ensureFreeGiftQuantityIsOne({ allowDuringMutation: true });
    this.preserveEnrichedCartItems(previousItems);

    const prevKeys = new Set(previousItems.map(item => this.getLineItemKey(item)));
    const nextKeySet = new Set((cart.items || []).map(item => this.getLineItemKey(item)));
    const hasNewLines = [...nextKeySet].some(key => !prevKeys.has(key));
    const hasRemovedLines = [...prevKeys].some(key => !nextKeySet.has(key));

    const drawerActive = this._keepDrawerOpen || this.isOpen;

    if (drawerActive) {
      await this.enrichCartWithMetafields();
      const needsFullRender = this.needsFullDrawerRender(this.cart) || hasNewLines || hasRemovedLines;
      if (needsFullRender) {
        this.applyCartStateToUi({ source, allowFullRender: true });
      } else {
        this.syncQuantityDisplaysFromCart(null);
        this.updateProgressVisual();
        this.updateCartIcon();
      }
      this.syncDrawerOpenState(source);
      this.pauseAutoRefresh(6000);
    } else {
      await this.enrichCartWithMetafields();
      this.applyCartStateToUi({ source, allowFullRender: true });
      if (!this._isCartMutating && !this._normalizingGift) {
        this.updateProgress();
      }
    }
  }

  async loadCart(source = 'loadCart', { deferUi = false, fast = false } = {}) {
    const preserveDrawer = this._keepDrawerOpen || this.isOpen;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const previousCartItems = [...(this.cart?.items || [])];
      this.cart = await response.json();
      this.preserveEnrichedCartItems(previousCartItems);
      const fetchFingerprint = this.getCartFingerprint(this.cart);
      if (!fast) await this.ensureFreeGiftQuantityIsOne();
      await this.enrichCartWithMetafields();

      const refreshResponse = fast ? null : await fetch('/cart.js');
      if (refreshResponse?.ok) {
        const freshCart = await refreshResponse.json();
        if (this.getCartFingerprint(freshCart) !== fetchFingerprint) {
          const previousItems = [...(this.cart?.items || [])];
          this.cart = freshCart;
          await this.ensureFreeGiftQuantityIsOne();
          this.preserveEnrichedCartItems(previousItems);
          await this.enrichCartWithMetafields();
        }
      }

      if (!deferUi) {
        this.applyCartStateToUi({ source, allowFullRender: true });

        // Skip progress update and free gift check when removing free gifts to prevent loops
        if (!this.isRemovingFreeGift) {
          this.updateProgress();
          this.checkFreeGift();
        }

        this.updateCartIcon();
      }

      if (preserveDrawer) {
        this.syncDrawerOpenState(`loadCart:${source}`);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      // Gracefully handle cart loading failure
      this.cart = { items: [], total_price: 0, item_count: 0 };
      if (!deferUi) {
        this.render('loadCart:error');
      }
    }
  }

  async enrichCartWithMetafields() {
    if (!this.cart?.items?.length) return;

    // Unieke handles
    const handles = [...new Set(
      this.cart.items.map(i => i.handle || i.product_handle).filter(Boolean)
    )];

    // Reuse product data across cart reads, including concurrent app refreshes.
    const productsByHandle = {};
    await Promise.all(handles.map(async (handle) => {
      try {
        if (!this._productDataRequests.has(handle)) {
          const request = fetch(`/products/${handle}.js`)
            .then(res => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.json();
            })
            .catch(error => {
              this._productDataRequests.delete(handle);
              throw error;
            });
          this._productDataRequests.set(handle, request);
        }
        productsByHandle[handle] = await this._productDataRequests.get(handle);
      } catch (e) {
        console.error('Product fetch error for', handle, e);
      }
    }));

    // Items verrijken met variant price & compare_at_price
    this.cart.items = this.cart.items.map(item => {
      const handle = item.handle || item.product_handle;
      const product = productsByHandle[handle];
      const variant = product?.variants?.find(v => v.id === item.variant_id);

      const compareAt = Number(variant?.compare_at_price || 0);
      const variantPrice = Number(variant?.price ?? item.price ?? 0);

      return {
        ...item,
        _variant_price: variantPrice,
        _compare_at_price: compareAt
        // Let op: GEEN properties schrijven hier
      };
    });
  }

  async updateQuantity(lineKey, change) {
    if (this.isLoading) return;

    const currentItem = this.findCartItem(lineKey);
    if (!currentItem) return;

    const newQuantity = Math.max(0, currentItem.quantity + change);
    await this.setQuantity(lineKey, newQuantity);
  }

  preserveEnrichedCartItems(previousItems = []) {
    if (!this.cart?.items?.length || !previousItems.length) return;

    this.cart.items = this.cart.items.map(item => {
      const previousItem = previousItems.find(
        prev => this.getLineItemKey(prev) === this.getLineItemKey(item)
      );

      if (!previousItem) return item;

      return {
        ...item,
        _variant_price: previousItem._variant_price,
        _compare_at_price: previousItem._compare_at_price
      };
    });
  }

  async setQuantity(lineKey, quantity) {
    if (this.isLoading) return;

    const cartEl = document.querySelector(this.selectors.cart);
    if (this.isOpen || cartEl?.classList.contains('is-open')) {
      this._keepDrawerOpen = true;
      this.isOpen = true;
    }

    this._isCartMutating = true;
    this.setLoading(true);
    const previousItems = this.cart?.items ? [...this.cart.items] : [];
    let pendingRemoveSync = false;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      this.cart = await response.json();

      this.preserveEnrichedCartItems(previousItems);

      const structureChanged = this.hasCartStructureChanged(this.cart);
      this.applyCartStateToUi({ source: 'setQuantity', allowFullRender: structureChanged });

      this.updateProgressVisual();
      this.updateCartIcon();
      this.pauseAutoRefresh();

      if (quantity === 0) {
        pendingRemoveSync = true;
      } else {
        await this.syncGiftboxPromoQuantities();
        await this.ensureFreeGiftQuantityIsOne({ allowDuringMutation: true });

        if (this.hasCartStructureChanged(this.cart)) {
          this.applyCartStateToUi({ source: 'setQuantity:post-gift', allowFullRender: true });
        } else {
          this.syncQuantityDisplaysFromCart(String(lineKey));
        }
      }

      // Dispatch cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: this.cart, source: 'bd-quick-cart' }
      }));

      this.syncDrawerOpenState('setQuantity');
      this.pauseAutoRefresh(quantity === 0 ? 1500 : 6000);

      void this.enrichCartWithMetafields();
      if (!pendingRemoveSync) {
        window.setTimeout(() => {
          if (!this._isCartMutating && !this._normalizingGift) {
            this.checkFreeGift();
          }
        }, 0);
      }

    } catch (error) {
      console.error('Fehler beim Verändern der Anzahl:', error);
      this.showNotification('Fehler beim Verändern der Anzahl', 'error');
    } finally {
      this.setLoading(false);
      this._isCartMutating = false;
      this._blockOverlayCloseUntil = Date.now() + 1200;
    }

    if (pendingRemoveSync) {
      await this.syncDependentCartApps(previousItems, 'setQuantity:remove');

      const paidTotal = this.getCartTotalExcludingFreeGifts();
      if ((this.cart?.item_count ?? 0) === 0 || paidTotal === 0) {
        this.close('cart-empty-after-remove');
        return;
      }
    }

    await this.flushDeferredReconcile('setQuantity:deferred-reconcile');
    await this.ensureFreeGiftQuantityIsOne();

    if (pendingRemoveSync) {
      this.syncDrawerOpenState('setQuantity:post-remove-sync');
      window.setTimeout(() => {
        if (!this._isCartMutating && !this._normalizingGift) {
          this.checkFreeGift();
        }
      }, 0);
    }
  }

  syncQuantityDisplaysFromCart(changedLineKey) {
    if (!this.cart?.items?.length || this.cart.item_count === 0) {
      this.render('syncQuantityDisplaysFromCart:empty');
      return;
    }

    this.removeStaleLineItemsFromDom();

    const subtotal = document.querySelector(this.selectors.subtotal);
    if (subtotal) {
      subtotal.textContent = this.priceWithSymbolEnd(this.getCartTotalExcludingFreeGifts());
    }

    const discountsContainer = document.querySelector('.bd-quick-cart__discounts');
    if (discountsContainer) {
      discountsContainer.innerHTML = this.renderDiscounts(this.cart.cart_level_discount_applications);
    }

    let missingDropdown = false;

    this.cart.items.forEach(item => {
      const lineKey = this.getLineItemKey(item);
      const dropdown = this.findQtyDropdown(lineKey);

      if (!dropdown) {
        missingDropdown = true;
        return;
      }

      this.updateQuantityDropdown(dropdown, item.quantity);
    });

    if (missingDropdown) {
      this.render('syncQuantityDisplaysFromCart:fallback');
    } else {
      this.syncPromoGiftQtyDisplays();
    }
  }

  updateCartDisplayAfterChange(changedLineKey) {
    this.syncQuantityDisplaysFromCart(changedLineKey);
  }

  async refreshCartContent(options = {}) {
    if (this.isOpen && !options.forceSectionRefresh) {
      await this.softRefreshCart({ skipFreeGiftCheck: options.skipFreeGiftCheck ?? true });
      return;
    }

    await this.refreshCartSection(options);
  }

  async removeItem(lineKey) {
    await this.setQuantity(lineKey, 0);
  }

  async refreshCartSection(options = {}) {
    const shouldStayOpen = options.forceOpen ?? (this.isOpen || this._keepDrawerOpen);
    const preserveUi = options.preserveUi ?? shouldStayOpen;

    this._isRefreshingSection = true;
    this._isCartMutating = true;

    try {
      const contentEl = preserveUi
        ? document.querySelector('[data-cart][data-scroll-lock-ignore]')
        : null;
      const scrollTop = contentEl?.scrollTop ?? 0;
      const activeElement = preserveUi ? document.activeElement : null;

      const res = await fetch('/cart?sections=bd-quick-cart_section');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const html = data['bd-quick-cart_section'];
      const container = document.querySelector('#shopify-section-bd-quick-cart_section');

      if (container && typeof html === 'string') {
        container.innerHTML = html;
        this.readCartSettingsFromDom();
        this.initializeVariantSelectDefaults(container);
        this.observeUpsellsForVariantSelects();
        this.updateScrollButtons();
        this.maintainOpenState({ scrollTop, activeElement, forceOpen: shouldStayOpen });
        this.bindCartOpenObserver();
        this.installSectionRefreshGuard();
      }
    } catch (e) {
      console.error('Error refreshing cart section:', e);
    } finally {
      this._isCartMutating = false;
      this._isRefreshingSection = false;
      if (shouldStayOpen) {
        this.syncDrawerOpenState('refreshCartSection');
      }
    }
  }

  maintainOpenState({ scrollTop = 0, activeElement = null, forceOpen = false } = {}) {
    if (!this.isOpen && !forceOpen && !this._keepDrawerOpen) return;

    this.restoreDrawerOpenSilently('maintainOpenState');

    const contentEl = document.querySelector('[data-cart][data-scroll-lock-ignore]');
    if (contentEl && scrollTop > 0) {
      contentEl.scrollTop = scrollTop;
    }

    if (activeElement && document.contains(activeElement)) {
      activeElement.focus({ preventScroll: true });
    }
  }

  getCartFingerprint(cart) {
    if (!cart) return '';

    const items = (cart.items || [])
      .map(item => `${item.key || item.variant_id}:${item.quantity}:${item.line_price}`)
      .sort()
      .join('|');
    const discounts = (cart.cart_level_discount_applications || [])
      .map(discount => `${discount.title}:${discount.total_allocated_amount}`)
      .join('|');

    return `${cart.item_count}:${cart.total_price}:${items}:${discounts}`;
  }

  async reconcileCartFromServer(source = 'reconcileCartFromServer', {
    previousItems = null,
    allowFullRender = true,
    force = false
  } = {}) {
    if (this._reconcilingCart) {
      this._deferredReconcile = true;
      return false;
    }

    this._reconcilingCart = true;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return false;

      const freshCart = await response.json();
      const prev = previousItems || [...(this.cart?.items || [])];
      const fingerprintChanged = this.getCartFingerprint(freshCart) !== this.getCartFingerprint(this.cart);

      if (!fingerprintChanged) {
        if (force || this.needsFullDrawerRender(this.cart)) {
          this.applyCartStateToUi({ source, allowFullRender, force });
        }
        return false;
      }

      this.cart = freshCart;
      this.preserveEnrichedCartItems(prev);
      await this.enrichCartWithMetafields();

      const hasInvalidGiftQty = this.getThresholdFreeGiftItems().some(item => item.quantity !== 1);
      if (hasInvalidGiftQty) {
        await this.ensureFreeGiftQuantityIsOne({ allowDuringMutation: true });
      } else {
        this.applyCartStateToUi({ source, allowFullRender, force: force || fingerprintChanged });
        this.updateProgressVisual();
        this.updateCartIcon();
      }
      return true;
    } catch (error) {
      console.error('Error reconciling cart from server:', error);
      return false;
    } finally {
      this._reconcilingCart = false;
    }
  }

  scheduleCartReconcile(source = 'scheduleCartReconcile', { force = false } = {}) {
    if (this._isCartMutating || this._syncingDependentApps || this._reconcilingCart) {
      this._deferredReconcile = true;
      return;
    }

    void this.reconcileCartFromServer(source, { allowFullRender: true, force });
  }

  async flushDeferredReconcile(source = 'flushDeferredReconcile') {
    if (!this._deferredReconcile) return false;
    this._deferredReconcile = false;
    return this.reconcileCartFromServer(source, { allowFullRender: true, force: true });
  }

  async syncDependentCartApps(previousItems, source = 'syncDependentCartApps') {
    if (this._syncingDependentApps) return;
    this._syncingDependentApps = true;

    try {
      const runGiftboxSync = async () => {
        if (typeof window.GIFTBOX_CUSTOM_UPDATE_CART !== 'function') return;
        try {
          await window.GIFTBOX_CUSTOM_UPDATE_CART();
        } catch (error) {
          console.error('Giftbox cart sync failed:', error);
        }
      };

      const fetchAndApplyCart = async () => {
        const response = await fetch('/cart.js');
        if (!response.ok) return false;

        this.cart = await response.json();
        this.preserveEnrichedCartItems(previousItems);
        await this.enrichCartWithMetafields();
        return true;
      };

      await runGiftboxSync();
      await fetchAndApplyCart();

      if (this.getCartTotalExcludingFreeGifts() === 0 && this.getFreeGiftItems().length > 0) {
        await runGiftboxSync();
        await fetchAndApplyCart();
      }

      const drawerActive = this._keepDrawerOpen || this.isOpen;
      if (drawerActive) {
        this._keepDrawerOpen = true;
        this.isOpen = true;
      }

      // Section HTML swap replaces the drawer node and causes a visible close/reopen flash.
      // While the flyout is open, sync in place via reconcile instead.
      if (!drawerActive) {
        await this.refreshCartSection({
          forceOpen: true,
          preserveUi: true
        });
      }

      await this.reconcileCartFromServer(`${source}:finalize`, {
        previousItems,
        allowFullRender: true,
        force: true
      });

      this.rememberRenderedLineKeys();
      this.updateProgressVisual();
      this.updateCartIcon();

      if (drawerActive) {
        this.syncDrawerOpenState(`${source}:finalize`);
      }
    } finally {
      this._syncingDependentApps = false;
    }
  }

  async softRefreshCart({ skipFreeGiftCheck = false, source = 'softRefreshCart', fetchCart = false } = {}) {
    if (fetchCart) {
      try {
        const response = await fetch('/cart.js');
        if (response.ok) {
          const previousItems = [...(this.cart?.items || [])];
          this.cart = await response.json();
          await this.ensureFreeGiftQuantityIsOne({ allowDuringMutation: true });
          this.preserveEnrichedCartItems(previousItems);
        }
      } catch (e) {
        console.error('Error fetching cart for soft refresh:', e);
      }
    }

    await this.enrichCartWithMetafields();

    const drawerActive = this._keepDrawerOpen || this.isOpen;
    this.applyCartStateToUi({
      source,
      allowFullRender: !drawerActive || this.needsFullDrawerRender(this.cart)
    });

    if (!this.isRemovingFreeGift && !skipFreeGiftCheck) {
      this.checkFreeGift();
    }
  }

  async ensureFreeGiftQuantityIsOne({ allowDuringMutation = false } = {}) {
    if (this._normalizingGift) return;
    if (this._isCartMutating && !allowDuringMutation) return;

    const invalidGifts = this.getThresholdFreeGiftItems().filter(item => item.quantity !== 1);
    if (!invalidGifts.length) return;

    this._normalizingGift = true;

    try {
      for (const gift of invalidGifts) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.getLineItemKey(gift),
            quantity: 1
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const previousItems = [...(this.cart?.items || [])];
        this.cart = await cartResponse.json();
        this.preserveEnrichedCartItems(previousItems);
        this.applyCartStateToUi({
          source: 'free-gift:normalize',
          allowFullRender: true,
          force: true
        });
        this.syncPromoGiftQtyDisplays();
        this.updateProgressVisual();
        this.updateCartIcon();
      }

      this.ensureDrawerOpen('free-gift:normalize-complete');
    } catch (error) {
      console.error('Error normalizing free gift quantity:', error);
    } finally {
      this._normalizingGift = false;
    }
  }

  readCartSettingsFromDom() {
    const cartEl = document.querySelector(this.selectors.cart);
    if (!cartEl) return;

    this.freeGiftThreshold = parseFloat(cartEl.dataset.freeGiftThreshold) || this.freeGiftThreshold;
    this.freeGiftProduct = cartEl.dataset.freeGiftProduct || this.freeGiftProduct;
    this.autoAddFreeGift = cartEl.dataset.autoAddFreeGift !== 'false';
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    this.autoRefreshTimer = setInterval(() => this.tickAutoRefresh(), this.autoRefreshIntervalMs);
  }

  stopAutoRefresh() {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  async tickAutoRefresh() {
    if (!this.isOpen || this.isLoading || this.isAutoRefreshing || this.isAutoRefreshPaused()) return;
    if (document.visibilityState !== 'visible') return;
    if (document.querySelector('.bd-quick-cart__qty-dropdown.is-open')) return;

    this.isAutoRefreshing = true;
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;

      const newCart = await response.json();
      const nextFingerprint = this.getCartFingerprint(newCart);

      if (nextFingerprint === this.getCartFingerprint(this.cart)) {
        return;
      }

      const nextKeys = (newCart?.items || []).map(item => this.getLineItemKey(item)).join('|');
      const structureChanged = nextKeys !== this._lastRenderedLineKeys.join('|');

      const previousItems = [...(this.cart?.items || [])];
      this.cart = newCart;
      this.preserveEnrichedCartItems(previousItems);

      this.applyCartStateToUi({ source: 'tickAutoRefresh', allowFullRender: structureChanged });
      this.ensureDrawerOpen('tickAutoRefresh');
    } catch (e) {
      console.error('Error auto-refreshing cart:', e);
    } finally {
      this.isAutoRefreshing = false;
    }
  }

  async addUpsell(variantId) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await this.loadCart('addUpsell');
      this.ensureDrawerOpen('addUpsell');
      this.pauseAutoRefresh(6000);

      // Show success feedback
      this.showNotification('Product added to cart!');

    } catch (error) {
      console.error('Error adding upsell:', error);
      this.showNotification('Error adding product', 'error');
    } finally {
      this.setLoading(false);
    }
  }


  async addFreeGift() {
    if (!this.freeGiftProduct || this.isLoading) return;

    // Check if free gift is already in cart (comprehensive check)
    const freeGiftItems = this.cart.items.filter(item =>
      (item.properties && item.properties._free_gift === 'true') ||
      item.product_handle === this.freeGiftProduct ||
      item.variant_id === parseInt(this.freeGiftProduct)
    );

    // If we already have any free gift items, don't add another
    if (freeGiftItems.length > 0) {
      // If we have more than one, remove extras
      if (freeGiftItems.length > 1) {
        for (let i = 1; i < freeGiftItems.length; i++) {
          const itemKey = freeGiftItems[i].key || freeGiftItems[i].variant_id;
          await this.setQuantity(itemKey, 0);
        }
      }
      return;
    }

    try {
      // Get the free gift product variant
      const productResponse = await fetch(`/products/${this.freeGiftProduct}.js`);

      if (!productResponse.ok) {
        throw new Error(`HTTP error! status: ${productResponse.status}`);
      }

      const product = await productResponse.json();

      if (product && product.variants && product.variants.length > 0) {
        const variantId = product.variants[0].id;

        // Add with price override to 0
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
              '_free_gift': 'true'
            }
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        await this.loadCart();
        if (!this.isOpen) {
          await this.refreshCartSection();
        }
        // this.showNotification('Free gift added to your cart! 🎁');
      }
    } catch (error) {
      console.error('Error adding free gift:', error);
      this.showNotification('Error adding free gift', 'error');
    } 
  }

  checkFreeGift() {
    if (!this.freeGiftProduct || this._isCartMutating || this._normalizingGift) return;

    if (this._keepDrawerOpen || this.isOpen) {
      this._deferredFreeGiftCheck = true;
      return;
    }

    // Use cart total excluding free gifts for accurate threshold calculation
    const cartTotal = this.getCartTotalExcludingFreeGifts() / 100; // Convert from cents
    const freeGiftItems = this.getFreeGiftItems();

    const hasGift = freeGiftItems.length > 0;

    if (cartTotal >= this.freeGiftThreshold) {
      if (!hasGift) {
        // Add free gift when threshold is met and no gift exists
        this.addFreeGift();
      } else if (freeGiftItems.length > 1) {
        // If multiple free gifts exist, keep only one
        this.isRemovingFreeGift = true;

        // Keep the first one, schedule removal of others
        for (let i = 1; i < freeGiftItems.length; i++) {
          const itemKey = this.getLineItemKey(freeGiftItems[i]);
          this.setQuantity(itemKey, 0);
        }

        this.isRemovingFreeGift = false;
      }
    } else if (cartTotal < this.freeGiftThreshold && hasGift) {
      // Remove free gift when threshold is not met
      if (!this.isRemovingFreeGift) {
        this.handleFreeGiftRemoval();
      }
    }
  }

  updateProgressVisual() {
    const progressFill = document.querySelector(this.selectors.progressFill);
    const progressText = document.querySelector(this.selectors.progressText);

    if (!progressFill || !progressText) return;

    // Calculate cart total excluding free gifts
    const cartTotal = this.getCartTotalExcludingFreeGifts() / 100; // Convert from cents
    const remaining = Math.max(0, this.freeGiftThreshold - cartTotal);
    const percentage = Math.min(100, (cartTotal / this.freeGiftThreshold) * 100);

    progressFill.style.width = `${percentage}%`;

    if (cartTotal < this.freeGiftThreshold) {
      const container = document.querySelector('[data-bd-quick-cart]');
      const prefix = container?.dataset.progressPrefix || 'Noch';
      const suffix = container?.dataset.progressSuffix || 'für gratis Geschenk';
      progressText.innerHTML = `<span>${prefix} €${remaining.toFixed(2)} ${suffix}</span>`;
    } else {
      const container = document.querySelector('[data-bd-quick-cart]');
      const unlockedText = container?.dataset.progressUnlocked || 'Gratis Geschenk freigeschaltet!';
      progressText.innerHTML = `<span class="bd-quick-cart__gift-unlocked p-xs">${unlockedText}</span>`;
    }
  }

  updateProgress() {
    this.updateProgressVisual();

    if (this._isCartMutating || this._normalizingGift) return;

    if (this._keepDrawerOpen || this.isOpen) {
      this._deferredFreeGiftCheck = true;
      return;
    }

    const cartTotal = this.getCartTotalExcludingFreeGifts() / 100;

    if (cartTotal < this.freeGiftThreshold) {
      if (!this.isRemovingFreeGift) {
        this.handleFreeGiftRemoval();
      }
    } else {
      this.handleFreeGiftAutoAdd();
    }
  }

  // Calculate cart total excluding free gift items
  getCartTotalExcludingFreeGifts() {
    if (!this.cart?.items) return 0;

    return this.cart.items.reduce((total, item) => {
      if (this.isFreeGiftItem(item)) {
        return total;
      }
      return total + (item.line_price || 0);
    }, 0);
  }

  // Handle automatic free gift removal when threshold not met
  async handleFreeGiftRemoval() {
    if (!this.cart?.items || this.isRemovingFreeGift) return;

    const freeGiftItems = this.getFreeGiftItems();

    if (freeGiftItems.length === 0) return;

    this.isRemovingFreeGift = true;

    try {
      // Remove all free gift items
      for (const freeGiftItem of freeGiftItems) {
        // Use the line item key (which could be variant_id or key depending on cart structure)
        const itemKey = freeGiftItem.key || freeGiftItem.variant_id;

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Cart API Error:', response.status, errorText);
          throw new Error(`Failed to remove free gift: ${response.status} - ${errorText}`);
        }
      }

      this.freeGiftAdded = false;

      // Update cart state without triggering progress update
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        this.cart = await cartResponse.json();
        await this.ensureFreeGiftQuantityIsOne();
        this.applyCartStateToUi({ source: 'handleFreeGiftRemoval', allowFullRender: true });
        this.updateCartIcon();
      }

    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this.isRemovingFreeGift = false;
      // Update progress after removal is complete
      this.updateProgress();
    }
  }

  // Handle automatic free gift addition
  async handleFreeGiftAutoAdd() {
    if (!this.autoAddFreeGift || !this.freeGiftProduct || this.freeGiftAdded) return;

    // Check if free gift is already in cart
    const freeGiftItems = this.cart?.items?.filter(item =>
      (item.properties && item.properties._free_gift === 'true') ||
      item.product_handle === this.freeGiftProduct ||
      item.variant_id === parseInt(this.freeGiftProduct)
    );

    // If we already have at least one free gift item, set flag and return
    if (freeGiftItems && freeGiftItems.length > 0) {
      this.freeGiftAdded = true;

      // If we have more than one free gift, remove the extras
      if (freeGiftItems.length > 1) {
        // Keep the first one, remove the rest
        for (let i = 1; i < freeGiftItems.length; i++) {
          const itemKey = freeGiftItems[i].key || freeGiftItems[i].variant_id;
          await this.setQuantity(itemKey, 0);
        }
      }
      return;
    }

    try {
      // Get product info to find variant ID
      const productResponse = await fetch(`/products/${this.freeGiftProduct}.js`);
      const product = await productResponse.json();

      if (product && product.variants && product.variants.length > 0) {
        const variantId = product.variants[0].id;

        // Add free gift with custom properties
        const formData = {
          'items': [{
            'id': variantId,
            'quantity': 1,
            'properties': {
              '_free_gift': 'true',
              '_original_price': product.variants[0].price,
              '_gift_threshold': this.freeGiftThreshold
            }
          }]
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          this.freeGiftAdded = true;

          // Apply discount to make the free gift $0
          await this.applyFreeGiftDiscount(variantId);

          // Update cart to reflect changes
          await this.loadCart();
          if (!this.isOpen) {
            await this.refreshCartSection();
          }
        }
      }
    } catch (error) {
      console.error('Error adding free gift:', error);
    } 
  }

  // Apply discount to make free gift $0
  async applyFreeGiftDiscount(variantId) {
    try {
      // Get the current cart to find the free gift item
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const freeGiftItem = cart.items.find(item =>
        item.variant_id === variantId &&
        item.properties &&
        item.properties._free_gift === 'true'
      );

      if (freeGiftItem && freeGiftItem.price > 0) {
        // Update the item with zero price using cart/change.js
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
              '_free_gift': 'true',
              '_original_price': freeGiftItem.price,
              '_gift_threshold': this.freeGiftThreshold,
              '_zero_price': 'true'
            }
          })
        });
      }
    } catch (error) {
      console.error('Error applying free gift discount:', error);
    }
  }

  escapeHtml(s) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  renderDrawerSafeSync(source = 'renderDrawerSafeSync') {
    this.scheduleCartReconcile(source, { force: true });
  }

  installGiftboxHook() {
    if (window.__bdGiftboxHookInstalled) return;
    window.__bdGiftboxHookInstalled = true;

    const wrapGiftboxHandler = (fn) => {
      if (typeof fn !== 'function') return fn;

      return async function giftboxWrapped(...args) {
        const cart = window.bdQuickCart;

        const keysBeforeApply = cart
          ? (cart.cart?.items || []).map(item => cart.getLineItemKey(item))
          : [];
        const fingerprintBefore = cart ? cart.getCartFingerprint(cart.cart) : '';

        const result = await fn.apply(this, args);

        if (!cart) {
          return result;
        }

        const paintUi = cart._keepDrawerOpen || cart.isOpen;
        if (paintUi) {
          cart._keepDrawerOpen = true;
        }

        const memoryKeysBeforeFetch = (cart.cart?.items || []).map(item => cart.getLineItemKey(item));
        let staleFetch = false;

        try {
          const response = await fetch('/cart.js');
          if (response.ok) {
            const previousItems = [...(cart.cart?.items || [])];
            const serverCart = await response.json();
            const serverKeys = (serverCart.items || []).map(item => cart.getLineItemKey(item));
            staleFetch = memoryKeysBeforeFetch.some(key => !serverKeys.includes(key));

            if (!staleFetch) {
              cart.cart = serverCart;
              cart.preserveEnrichedCartItems(previousItems);
            } else {
              cart.scheduleCartReconcile('giftbox:stale-fetch', { force: true });
            }
          }
        } catch (error) {
          console.error('Giftbox cart fetch failed:', error);
        }

        await cart.ensureFreeGiftQuantityIsOne({ allowDuringMutation: true });

        const fingerprintAfter = cart.getCartFingerprint(cart.cart);
        const paidTotal = cart.getCartTotalExcludingFreeGifts();
        const hasGift = cart.getFreeGiftItems().length > 0;
        const keysAfter = (cart.cart?.items || []).map(item => cart.getLineItemKey(item));
        const removalDetected = keysAfter.length < keysBeforeApply.length;
        const staleMissingPromoGift = paidTotal > 0 && !hasGift;
        const fingerprintChanged = fingerprintBefore !== fingerprintAfter;
        const needsDomSync = cart.needsFullDrawerRender(cart.cart);

        const shouldPaintUi = paintUi && !cart._deferCartUiPaint && !staleMissingPromoGift && (
          removalDetected ||
          (fingerprintChanged && (hasGift || paidTotal === 0)) ||
          (needsDomSync && (removalDetected || hasGift || paidTotal === 0))
        );

        if (shouldPaintUi) {
          cart.applyCartStateToUi({
            source: 'giftbox:after-hook',
            allowFullRender: true,
            force: true
          });
        } else if (staleMissingPromoGift) {
          cart.scheduleCartReconcile('giftbox:await-gift', { force: true });
        } else if (!paintUi && fingerprintChanged) {
          cart.scheduleCartReconcile('giftbox:pending-ui', { force: true });
        } else if (paintUi) {
          cart.syncPromoGiftQtyDisplays();
        }

        if (paintUi) {
          cart.syncDrawerOpenState('giftbox:after-hook');
        }

        return result;
      };
    };

    let giftboxHandler = window.GIFTBOX_CUSTOM_UPDATE_CART;
    if (typeof giftboxHandler === 'function') {
      giftboxHandler = wrapGiftboxHandler(giftboxHandler);
      window.GIFTBOX_CUSTOM_UPDATE_CART = giftboxHandler;
    }

    Object.defineProperty(window, 'GIFTBOX_CUSTOM_UPDATE_CART', {
      configurable: true,
      enumerable: true,
      get() {
        return giftboxHandler;
      },
      set(fn) {
        giftboxHandler = wrapGiftboxHandler(fn);
      }
    });
  }

  render(source = 'unknown', { force = false } = {}) {
    if (this._deferCartUiPaint && !force) return;

    const drawerActive = this._keepDrawerOpen || this.isOpen;
    const needsFullRender = this.needsFullDrawerRender(this.cart);

    // Giftbox/Memberr call render() with no source — never trust in-memory cart.
    if (drawerActive && !force && source === 'unknown') {
      this.scheduleCartReconcile('render:unknown', { force: true });
      return;
    }

    if (drawerActive && !needsFullRender && !force) {
      this.applyCartStateToUi({ source: `render:${source}`, allowFullRender: false });
      this.syncDrawerOpenState(`render:${source}`);
      return;
    }

    const itemsContainer = document.querySelector(this.selectors.items);
    const emptyState = document.querySelector(this.selectors.empty);
    const footer = document.querySelector(this.selectors.footer);
    const subtotal = document.querySelector(this.selectors.subtotal);

    if (!itemsContainer) return;

    const renderFingerprint = this.getCartFingerprint(this.cart);

    // Update item count and total
    if (subtotal) {
      // Display total excluding free gifts
      const totalExcludingGifts = this.getCartTotalExcludingFreeGifts();
      subtotal.textContent = this.priceWithSymbolEnd(totalExcludingGifts);
    }

    // Show/hide empty state and footer
    if (this.cart.item_count === 0) {
      if (emptyState) emptyState.style.display = 'block';
      if (footer) footer.style.display = 'none';
      itemsContainer.innerHTML = '';
      if (this.getCartFingerprint(this.cart) === renderFingerprint) {
        this.rememberRenderedLineKeys();
      }
      return;
    } else {
      if (emptyState) emptyState.style.display = 'none';
      if (footer) footer.style.display = 'block';
    }

    // Render items — animate only newly added lines
    const previousKeys = new Set(this._lastRenderedLineKeys);
    itemsContainer.innerHTML = this.cart.items.map(item => {
      const lineKey = this.getLineItemKey(item);
      return this.renderItem(item, { animate: !previousKeys.has(lineKey) });
    }).join('');

    if (this.getCartFingerprint(this.cart) === renderFingerprint) {
      this.rememberRenderedLineKeys();
    }

    // Updated by memberr
    // Render discounts
    let discountsContainer = document.querySelector('.bd-quick-cart__discounts')
    if(!discountsContainer) {
      const div = document.createElement("div")
      div.className = "bd-quick-cart__discounts"
      footer.prepend(div)
      discountsContainer = div
    }
    discountsContainer.innerHTML = this.renderDiscounts(this.cart.cart_level_discount_applications)

    // Initialize scroll buttons after rendering
    setTimeout(() => this.updateScrollButtons(), 100);

    if (this._keepDrawerOpen || this.isOpen) {
      this.syncDrawerOpenState(`render:${source}`);
    }
  }

  priceWithSymbolEnd(cents, symbol) {
    const sym = symbol || window.CURRENCY_SYMBOL || '$';
    const n = Number(cents) / 100;
    return n.toFixed(2).replace('.', ',') + ' ' + sym;
  }

  renderQuantityDropdown(item) {
    return `
      <div class="bd-quick-cart__quantity">
        <div class="bd-quick-cart__qty-dropdown" data-qty-dropdown data-line-key="${this.getLineItemKey(item)}" data-variant-id="${item.variant_id}">
          <button type="button" class="bd-quick-cart__qty-trigger p-xs" data-qty-trigger>
            <span class="bd-quick-cart__qty-value" data-qty-value>${item.quantity}</span>
            <div class="bd-quick-cart__qty-icons">
              <div class="bd-quick-cart__qty-icon"><svg xmlns="http://www.w3.org/2000/svg" width="9" height="4" viewBox="0 0 9 4" fill="none">
<path d="M0.5 1L4.5 3L8.5 1" stroke="#2E2E2E" stroke-width="0.6" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
              </div>
            </div>
          </button>
          <div class="bd-quick-cart__qty-options" data-qty-options>
            <div class="bd-quick-cart__qty-option" data-qty-option="0">
              <span class="bd-quick-cart__qty-option-text">0</span>
            </div>
            ${Array.from({ length: this.getMaxQuantity() }, (_, i) => i + 1).map(i =>
      `<div class="bd-quick-cart__qty-option${item.quantity === i ? ' bd-quick-cart__qty-option--selected' : ''}" data-qty-option="${i}">
              <span class="bd-quick-cart__qty-option-text">${i}</span>
            </div>`
    ).join('')}
          </div>
        </div>
      </div>
    `;
  }

  renderItem(item, { animate = false } = {}) {
    const isGift = this.isFreeGiftItem(item);
    const compareAt = Number(item._compare_at_price || 0);
    const currentPrice = Number(item.price ?? item._variant_price ?? 0);
    const hasComparePrice = compareAt > currentPrice;
    const discountPct = hasComparePrice
      ? Math.round(((compareAt - currentPrice) / compareAt) * 100)
      : 0;
    const enterClass = animate ? ' bd-quick-cart__item--enter' : '';

    return `
      <div class="bd-quick-cart__item${isGift ? ' bd-quick-cart__item--gift' : ''}${enterClass}" data-line-key="${this.getLineItemKey(item)}" data-variant-id="${item.variant_id}">
        <a href="${item.url}" class="bd-quick-cart__item-image">
          ${item.image ? `
            <img 
              src="${item.image}" 
              alt="${item.product_title}"
              width="80"
              height="80"
              loading="lazy"
            >
          ` : ''}
        
          
        
        </a>
        
        <div class="bd-quick-cart__item-details">
          <div class="bd-quick-cart__item-info">
            <h3 class="bd-quick-cart__item-title p-m">
              <a href="${item.url}" class="bd-quick-cart__item-title-link p-m">${item.product_title}</a>
            </h3>
            ${item.variant_title !== 'Default Title' ? `
              <p class="bd-quick-cart__item-variant p-xs">${item.variant_title}</p>
            ` : ''}
            ${isGift ? `
              
            ` : ''}<div class="bd-quick-cart__item-price">
              ${isGift || item.price === 0 ? `
            <div class="bd-quick-cart__badge p-xxs bd-quick-cart__badge--free"
            >
              Gratis
            </div>
          ` : ''}

  <span class="bd-quick-cart__item-prices">
  ${hasComparePrice ? `
    <span class="bd-quick-cart__item-price-compare p-xxs">
      ${this.priceWithSymbolEnd(compareAt)}
    </span>
  ` : ``}
  ${isGift || item.price === 0 ? `
    <span class="bd-quick-cart__item-price-current bd-quick-cart__item-price-current-free">
      ${this.priceWithSymbolEnd(currentPrice)}
    </span>
  ` : `
    <span class="bd-quick-cart__item-price-current">
      ${this.priceWithSymbolEnd(currentPrice)}
    </span>
  `}

  </span>
  ${hasComparePrice ? `
    <div class="bd-quick-cart__badge p-xxs bd-quick-cart__badge--sale">
      ${discountPct}% Sale
    </div>
  ` : ''}

</div>
          ${this.renderProductFeatures(item)}
          </div>
          
          
        </div>
        
        <div class="bd-quick-cart__item-actions">
          ${this.renderQuantityDropdown(item)}
        </div>
      </div>
    `;
  }

  async handleAddToCartAndOpen(button) {
    if (this.isLoading) return;

    this.setLoading(true);

    try {
      // Find the product form
      const form = button.closest('form[data-product-form]');
      if (!form) {
        console.error('Product form not found');
        this.open();
        return;
      }

      const formData = new FormData(form);



      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Update cart and open drawer
      this._keepDrawerOpen = true;
      await this.loadCart('handleAddToCartAndOpen');
      await this.enrichCartWithMetafields();
      this.render('handleAddToCartAndOpen', { force: true });
      this.ensureDrawerOpen('handleAddToCartAndOpen');
      this.pauseAutoRefresh(6000);

      // Show success notification
      this.showNotification('Product added to cart!', 'success');

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Error adding product to cart', 'error');
      // Still open cart even if add fails
      this.open();
    } finally {
      this.setLoading(false);
    }
  }

  open() {
    this._keepDrawerOpen = true;

    const cartElement = document.querySelector(this.selectors.cart);
    if (!cartElement) return;

    const wasOpen = cartElement.classList.contains('is-open');
    if (!wasOpen) {
      cartElement.classList.add('bd-quick-cart--no-transition');
    }
    cartElement.classList.add('is-open');
    this.isOpen = true;
    this.lockPageScroll();

    if (!wasOpen) {
      void cartElement.offsetHeight;
      cartElement.classList.remove('bd-quick-cart--no-transition');
      const closeBtn = cartElement.querySelector(this.selectors.closeBtn);
      if (closeBtn) {
        closeBtn.focus();
      }
      this.startAutoRefresh();
    }
  }

  close(reason = 'unknown') {
    this._keepDrawerOpen = false;

    const cartElement = document.querySelector(this.selectors.cart);
    if (cartElement) {
      cartElement.classList.remove('is-open');
      this.isOpen = false;
      this.unlockPageScroll();

      this.stopAutoRefresh();

      // Ensure cart icon is updated with current cart state when closing
      this.updateCartIcon();
    } else {
      this.isOpen = false;
      this.unlockPageScroll();
    }

    this.flushDeferredGiftSideEffects();
  }

  setLoading(loading) {
    this.isLoading = loading;
    const cartElement = document.querySelector(this.selectors.cart);
    if (cartElement) {
      cartElement.classList.toggle('bd-quick-cart__loading', loading);
    }
  }

  toggleQuantityDropdown(dropdown) {
    const isOpen = dropdown.classList.contains('is-open');

    // Close all other dropdowns first
    this.closeAllQuantityDropdowns();

    // Toggle current dropdown
    if (!isOpen) {
      dropdown.classList.add('is-open');
    }
  }

  closeAllQuantityDropdowns() {
    const dropdowns = document.querySelectorAll(this.selectors.qtyDropdown);
    dropdowns.forEach(dropdown => {
      dropdown.classList.remove('is-open');
    });
  }

  selectQuantityOption(dropdown, selectedOption, newQty) {
    // Update the display value
    const valueElement = dropdown.querySelector(this.selectors.qtyValue);
    if (valueElement) {
      valueElement.textContent = newQty;
    }

    // Update selected state
    const options = dropdown.querySelectorAll(this.selectors.qtyOption);
    options.forEach(option => {
      option.classList.remove('bd-quick-cart__qty-option--selected');
    });
    selectedOption.classList.add('bd-quick-cart__qty-option--selected');

    // Close dropdown
    dropdown.classList.remove('is-open');
  }

  updateCartIcon() {
    // Calculate total quantity (sum of all item quantities)
    let totalQuantity = 0;
    if (this.cart && this.cart.items) {
      totalQuantity = this.cart.items.reduce((sum, item) => sum + item.quantity, 0);
    }

    // Use setTimeout to ensure our update happens after theme.js cart:updated event
    setTimeout(() => {
      // Update cart icon count in header
      const cartCountElements = document.querySelectorAll('[data-js-cart-count]');

      cartCountElements.forEach(element => {
        element.innerHTML = totalQuantity;
      });

      // Update cart indicator visibility
      const cartIndicators = document.querySelectorAll('[data-js-cart-indicator]');
      cartIndicators.forEach(indicator => {
        if (totalQuantity > 0) {
          indicator.style.display = 'block';
        } else {
          indicator.style.display = 'none';
        }
      });

      // Force a repaint to ensure the update is visible
      cartCountElements.forEach(element => {
        element.offsetHeight; // Trigger reflow
      });
    }, 100); // Small delay to ensure theme.js updates first
  }

  getMaxQuantity() {
    const maxQtyElement = document.querySelector('[data-bd-cart-max-qty]');
    return maxQtyElement ? parseInt(maxQtyElement.dataset.bdCartMaxQty) || 10 : 10;
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);

    // Add space between currency symbol and amount
    return formatted.replace('€', '€ ');
  }

  renderProductFeatures(item) {
    const p = item.properties || {};
    // console.log("BD FEAT ===========");
    // console.log(p);
    const f1 = {
      text: p['_Feature_1_text'],
      icon: p['_Feature_1_icon'],
      alt: p['_Feature_1_icon_alt']
    };
    const f2 = {
      text: p['_Feature_2_text'],
      icon: p['_Feature_2_icon'],
      alt: p['_Feature_2_icon_alt']
    };
    const feats = [f1, f2].filter(f => f && (f.text || f.icon));
    if (!feats.length) return '';

    return `
    <div class="product-feature-list-cart-drawer">
      ${feats.map(f => `
        <div class="product-feature-list__item">
          <div class="product-feature-list__icon">
            ${f.icon ? `
              <img src="${f.icon}" alt="${(f.alt || '').replace(/"/g, '&quot;')}" loading="lazy" width="22" height="22">
            ` : ''}
          </div>
          <div class="product-feature-list__text p-xs">
            ${f.text ? this.escapeHtml(String(f.text)) : ''}
          </div>
        </div>
      `).join('')}
    </div>
  `;
  }



  showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `bd-notification bd-notification--${type}`;
    notification.textContent = message;

    // Style the notification
    Object.assign(notification.style, {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '6px',
      backgroundColor: type === 'error' ? '#ff4444' : '#00aa44',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      zIndex: '10000',
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    });

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 10);

    // Remove after delay
    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  // Upsell carousel scroll functionality
  scrollUpsells(direction) {
    const track = document.querySelector('[data-upsells-track]');
    if (!track) return;

    const cardWidth = 140 + 12; // card width + gap
    const scrollAmount = cardWidth * 2; // scroll 2 cards at a time

    if (direction === 'prev') {
      track.scrollLeft -= scrollAmount;
    } else {
      track.scrollLeft += scrollAmount;
    }

    // Update scroll button states
    setTimeout(() => this.updateScrollButtons(), 100);
  }

  // Update scroll button visibility and state
  updateScrollButtons() {
    const track = document.querySelector('[data-upsells-track]');
    const prevBtn = document.querySelector('[data-scroll-prev]');
    const nextBtn = document.querySelector('[data-scroll-next]');

    if (!track || !prevBtn || !nextBtn) return;

    const isAtStart = track.scrollLeft <= 0;
    const isAtEnd = track.scrollLeft >= (track.scrollWidth - track.clientWidth - 1);

    prevBtn.disabled = isAtStart;
    nextBtn.disabled = isAtEnd;
  }

  // Initialize touch/swipe support for carousel
  initUpsellCarouselTouch() {
    let startX = 0;
    let scrollLeft = 0;
    let isDown = false;

    document.addEventListener('mousedown', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track) return;

      isDown = true;
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
      track.style.cursor = 'grabbing';
    });

    document.addEventListener('mouseleave', () => {
      isDown = false;
      const track = document.querySelector('[data-upsells-track]');
      if (track) track.style.cursor = 'cursor';
    });

    document.addEventListener('mouseup', () => {
      isDown = false;
      const track = document.querySelector('[data-upsells-track]');
      if (track) {
        track.style.cursor = 'cursor';
        this.updateScrollButtons();
      }
    });

    document.addEventListener('mousemove', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track || !isDown) return;

      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 2;
      track.scrollLeft = scrollLeft - walk;
    });

    // Touch events for mobile
    document.addEventListener('touchstart', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track) return;

      startX = e.touches[0].pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      const track = e.target.closest('[data-upsells-track]');
      if (!track) return;

      const x = e.touches[0].pageX - track.offsetLeft;
      const walk = (x - startX) * 2;
      track.scrollLeft = scrollLeft - walk;
    }, { passive: true });

    document.addEventListener('touchend', () => {
      setTimeout(() => this.updateScrollButtons(), 100);
    });

    // Update scroll buttons on scroll
    document.addEventListener('scroll', (e) => {
      if (e.target && typeof e.target.matches === 'function' && e.target.matches('[data-upsells-track]')) {
        this.updateScrollButtons();
      }
    }, true);
  }

  // ADD: ===== Upsell Variant Select helpers =====
  updateUpsellButtonForSelect(selectEl) {
    if (!selectEl) return;

    const card = selectEl.closest('.bd-quick-cart__upsell-card');
    if (!card) return;

    const addBtn = card.querySelector(this.selectors.addUpsell);
    if (!addBtn) return;

    const productTitle = selectEl.getAttribute('data-product-title') || '';
    const option = selectEl.selectedOptions && selectEl.selectedOptions[0];
    if (!option) return;

    const variantId = option.value;
    const variantTitle = option.getAttribute('data-variant-title') || '';
    const variantImage = option.getAttribute('data-variant-image') || '';

    // Update existing button (do NOT replace it)
    addBtn.setAttribute('data-variant-id', variantId);

    // Keep original aria-label prefix, append product + variant
    const baseAria = addBtn.getAttribute('aria-label') || 'Add to cart';
    const prefix = (baseAria.split(' - ')[0] || baseAria).trim();
    const label = variantTitle
      ? `${prefix} - ${productTitle} (${variantTitle})`
      : `${prefix} - ${productTitle}`;
    addBtn.setAttribute('aria-label', label);

    // Disable add button if the variant option is disabled (sold out)
    const isDisabled = option.disabled === true;
    addBtn.toggleAttribute('disabled', isDisabled);
    addBtn.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

    // Update the product image if variant image is available
    if (variantImage) {
      const productImage = card.querySelector('.bd-quick-cart__product-image img');
      if (productImage) {
        productImage.src = variantImage;
        productImage.srcset = variantImage;
      }
    }
  }

  initializeVariantSelectDefaults(scope) {
    const root = scope || document;
    const selects = root.querySelectorAll(this.selectors.variantSelect);
    selects.forEach((selectEl) => this.updateUpsellButtonForSelect(selectEl));
  }

  observeUpsellsForVariantSelects() {
    // Observe upsell container for new cards (e.g., drawer re-renders, locale changes)
    const container = document.querySelector('[data-upsells-container]');
    if (!container || this._upsellsObserver) return;

    this._upsellsObserver = new MutationObserver(() => {
      // Re-initialize any new <select data-upsell-variant-select>
      this.initializeVariantSelectDefaults(container);
    });

    this._upsellsObserver.observe(container, { childList: true, subtree: true });
  }

  // Updated by memberr
  // Render discounts 
  renderDiscounts(cartLevelDiscountApplications) {
    return `
    <div class="bd-quick-cart__discounts">
    ${cartLevelDiscountApplications.map((d) => `
      <div class="bd-quick-cart__discount">
          <span class="bd-quick-cart__discount-title">${d.title}</span>
          <span class="bd-quick-cart__discount-amount">-${this.priceWithSymbolEnd(d.total_allocated_amount)}</span>
      </div>
      `)}
    </div>
    `
  }
}

// Initialize BD Quick Cart
document.addEventListener('DOMContentLoaded', () => {
  window.bdQuickCart = new BDQuickCart();
  window.bdQuickCart.installGiftboxHook();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BDQuickCart;
}
